import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import Grid from "@material-ui/core/Grid";
import ButtonBase from "@material-ui/core/ButtonBase";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import AppsIcon from "@material-ui/icons/Apps";
import AndroidIcon from "@material-ui/icons/Android";
import DriveEtaIcon from "@material-ui/icons/DriveEta";
import LockIcon from "@material-ui/icons/Lock";
import axios from "axios";
import { useHistory } from "react-router-dom";
import useUserState from "../hooks/useUserState";

// Apps available from the navbar "Apps" icon. Each entry is shown only if
// its backend feature flag (fetched from /{configEndpoint}) is enabled, and
// is locked in the popover for any role not listed in allowedRoles.
const APPS = [
  {
    key: "driver_medical_certificate",
    label: "Comprar Dictamen Médico",
    icon: DriveEtaIcon,
    route: "/patient/driver-medical-certificate",
    configEndpoint: "driver-medical-certificate/config",
    allowedRoles: ["PATIENT"],
  },
];

const ANDROID_APP = {
  key: "android_download",
  label: "Descargar app Android",
  icon: AndroidIcon,
  downloadUrl:
    "https://hcg-general-public.s3.us-east-2.amazonaws.com/app-release.apk",
};

const TILE_SIZE = 96;

export default function AppsMenu({ className }) {
  const history = useHistory();
  const [userState] = useUserState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [availableApps, setAvailableApps] = useState([]);

  const userRole = userState?.user?.role?.name?.toUpperCase();

  useEffect(() => {
    let isMounted = true;

    async function loadAvailableApps() {
      const results = await Promise.all(
        APPS.map(async (app) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL_BASE}/${app.configEndpoint}`,
              {
                headers: { Authorization: `Bearer ${userState?.jwt}` },
              }
            );
            return response.data?.enabled ? app : null;
          } catch (error) {
            return null;
          }
        })
      );
      if (isMounted) {
        setAvailableApps(results.filter(Boolean));
      }
    }

    if (userState?.jwt) {
      loadAvailableApps();
    }

    return () => {
      isMounted = false;
    };
  }, [userState?.jwt]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (app) => {
    if (!app.allowedRoles.includes(userRole)) return;
    handleClose();
    history.push(app.route);
  };

  return (
    <>
      <Button className={className} onClick={handleOpen}>
        <AppsIcon style={{ color: "white" }} />
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: 8,
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            marginTop: 8,
          },
        }}
      >
        <Grid
          container
          spacing={1}
            style={{ padding: 16, maxWidth: TILE_SIZE * 3 + 32 }}
          >
          {[...availableApps, ANDROID_APP].map((app) => {
            const isDownload = Boolean(app.downloadUrl);
            const isAllowed = isDownload || app.allowedRoles.includes(userRole);
            const tile = (
              <ButtonBase
                {...(isDownload
                  ? {
                      component: "a",
                      href: app.downloadUrl,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }
                  : { onClick: () => handleSelect(app) })}
                disabled={!isAllowed}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: TILE_SIZE,
                  height: TILE_SIZE,
                  padding: 8,
                  borderRadius: 8,
                  opacity: isAllowed ? 1 : 0.4,
                  textDecoration: "none",
                }}
              >
                {isAllowed ? (
                  <app.icon
                    fontSize="large"
                    htmlColor={isDownload ? "#3ddc84" : "#616161"}
                  />
                ) : (
                  <LockIcon fontSize="large" htmlColor="#9e9e9e" />
                )}
                <Typography
                  variant="caption"
                  align="center"
                  style={{ marginTop: 4, lineHeight: 1.2 }}
                >
                  {app.label}
                </Typography>
              </ButtonBase>
            );

            return (
              <Grid item key={app.key}>
                {isAllowed ? (
                  tile
                ) : (
                  <Tooltip title="Disponible solo para pacientes">
                    <span>{tile}</span>
                  </Tooltip>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Popover>
    </>
  );
}
